import { useState } from 'react';
import { Formik } from 'formik';
import { Box, TextProps } from '@chakra-ui/react';
import { validateEmail, validateRequired } from 'utils/validation';
import { useForgotPassword } from 'hooks';
import { errorMessageEmailInvalid, errorMessageRecaptchaFailed } from 'constants/validation';
import { Button, Input, FormControl, Recaptcha, Text } from 'components/UI';

type ForgotPasswordFormProps = {
  url: string;
  infoProps?: TextProps;
  repeatProps?: TextProps;
};

type ForgotPasswordMessageProps = {
  children: React.ReactNode;
  repeatProps?: TextProps;
};

type ForgotPasswordFormValues = {
  email: string;
  recaptcha: string;
};

const ForgotPasswordMessage = ({ children, ...props }: ForgotPasswordMessageProps) => (
  <Text mx={8} my={12} {...props}>
    {children}
  </Text>
);

const ForgotPasswordForm = ({ url, infoProps, repeatProps }: ForgotPasswordFormProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [sendForgotPassword, { data: message, error }] = useForgotPassword();

  const handleSendForgotPassword = async ({ email }: ForgotPasswordFormValues) => {
    const sendForgotPasswordResult = await sendForgotPassword({
      email,
      url,
    });

    if (sendForgotPasswordResult) {
      setEmailSent(true);
    }
  };

  if (error) {
    return <ForgotPasswordMessage>{error}</ForgotPasswordMessage>;
  }

  return emailSent ? (
    <ForgotPasswordMessage data-testid="forgot-password-success-message" {...repeatProps}>
      {message} Click&nbsp;
      <Button variant="link" onClick={() => setEmailSent(false)}>
        here
      </Button>
      &nbsp;to request another password reset.
    </ForgotPasswordMessage>
  ) : (
    <Formik
      initialValues={{ email: '', recaptcha: '' }}
      validate={async (values) => {
        const errors = validateRequired<ForgotPasswordFormValues>(['email'], values);
        if (values.email && !validateEmail(values.email)) {
          errors.email = errorMessageEmailInvalid;
        }

        if (!values.recaptcha) {
          errors.recaptcha = errorMessageRecaptchaFailed;
        }

        return errors;
      }}
      onSubmit={handleSendForgotPassword}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => {
        const emailError = submitted || touched.email ? errors.email : undefined;
        const recaptchaError = submitted ? errors.recaptcha : undefined;

        return (
          <form data-testid="forgot-password-form" onSubmit={handleSubmit}>
            <Text my={6} color="gray.400" {...infoProps}>
              Enter your account&apos;s email address below. <br /> If an account with that email exists, you&apos;ll
              receive a recovery email.
            </Text>
            <Box maxWidth={380} mx="auto">
              <FormControl isInvalid={!!emailError} mb={6}>
                <Input
                  type="email"
                  name="email"
                  maxLength={100}
                  value={values.email}
                  error={emailError}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your email"
                  textAlign="center"
                  errorProps={{
                    justifyContent: 'center',
                  }}
                />
              </FormControl>

              <FormControl isInvalid={!!recaptchaError}>
                <Recaptcha onChange={(token: string) => setFieldValue('recaptcha', token)} error={recaptchaError} />
              </FormControl>
              <Button
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                variant="solid"
                size="lg"
                type="submit"
                width="100%"
                onClick={() => setSubmitted(true)}
              >
                SUBMIT
              </Button>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};

export default ForgotPasswordForm;
